import axiosInstance from './AxiosInstance';

const CompaniesService = {
  // 1. CREATE COMPANY
  createCompany: async (companyData) => {
    try {
      const response = await axiosInstance.post('/companies', companyData);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create company',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 2. GET ALL COMPANIES
  getAllCompanies: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.city) queryParams.append('city', params.city);
      if (params.state) queryParams.append('state', params.state);
      if (params.country) queryParams.append('country', params.country);
      if (params.createdAt) queryParams.append('createdAt', params.createdAt);
      if (params.updatedAt) queryParams.append('updatedAt', params.updatedAt);

      const response = await axiosInstance.get(`/companies?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch companies',
        data: [],
        pagination: {}
      };
    }
  },

  // 3. GET ACTIVE COMPANIES
  getActiveCompanies: async () => {
    try {
      const response = await axiosInstance.get('/companies/active');
      return {
        success: response.data.success,
        data: response.data.data.companies,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch active companies',
        data: []
      };
    }
  },

  // 4. GET COMPANY BY ID
  getCompanyById: async (id) => {
    try {
      const response = await axiosInstance.get(`/companies/${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch company',
        data: null
      };
    }
  },

  // 5. UPDATE COMPANY
  updateCompany: async (id, companyData) => {
    try {
      const response = await axiosInstance.put(`/companies/${id}`, companyData);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update company',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 6. SOFT DELETE COMPANY
  deleteCompany: async (id) => {
    try {
      const response = await axiosInstance.delete(`/companies/${id}`);
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete company'
      };
    }
  },

  // 7. GET MY COMPANIES
  getMyCompanies: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      const response = await axiosInstance.get(`/companies/my-companies?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch my companies',
        data: [],
        pagination: {}
      };
    }
  },

  // 8. GET MY BRANCHES
  getMyBranches: async (companyId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      queryParams.append('companyId', companyId);

      const response = await axiosInstance.get(`/companies/my-branches?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch my branches',
        data: [],
        pagination: {}
      };
    }
  },

  // Legacy method names for backward compatibility
  getCompanies: async (params = {}) => {
    return await CompaniesService.getAllCompanies(params);
  },

  getCompaniesForSelect: async () => {
    return await CompaniesService.getActiveCompanies();
  },

  // Helper method for search
  searchCompanies: async (searchTerm) => {
    return await CompaniesService.getAllCompanies({ search: searchTerm, limit: 50 });
  },

  // Helper method for status filtering
  getCompaniesByStatus: async (status) => {
    try {
      const response = await axiosInstance.get(`/companies/status/${status}`);
      return {
        success: response.data.success,
        data: response.data.data.companies,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch companies by status',
        data: []
      };
    }
  },

  // Get companies for dropdown
  getCompanyDropdown: async () => {
    try {
      const response = await axiosInstance.get('/companies/dropdown');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch company dropdown',
        data: []
      };
    }
  },

  // Get companies statistics
  getCompaniesStatistics: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);

      const url = `/statistics/companies${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch companies statistics',
        data: {
          active_count: 0,
          inactive_count: 0,
          pending_count: 0,
          total_count: 0
        }
      };
    }
  },

  // 9. UPLOAD COMPANY LOGO
  uploadCompanyLogo: async (companyId, file) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await axiosInstance.post(`/companies/${companyId}/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload company logo',
        data: null
      };
    }
  },

  // 10. DELETE COMPANY LOGO
  deleteCompanyLogo: async (companyId) => {
    try {
      const response = await axiosInstance.delete(`/companies/${companyId}/logo`);
      
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete company logo',
        data: null
      };
    }
  }

  // 11. GET COMPANY PWA CONFIG
  ,getCompanyPwaConfig: async (companyId) => {
    try {
      const response = await axiosInstance.get(`/companies/${companyId}/pwa`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch company PWA configuration',
        data: null
      };
    }
  },

  // 12. UPDATE COMPANY PWA CONFIG
  updateCompanyPwaConfig: async (companyId, payload) => {
    try {
      const response = await axiosInstance.put(`/companies/${companyId}/pwa`, payload);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update company PWA configuration',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 13. UPLOAD PWA ICON (icon192 | icon512)
  uploadCompanyPwaIcon: async (companyId, iconType, file) => {
    try {
      const formData = new FormData();
      formData.append('icon', file);

      const response = await axiosInstance.post(`/companies/${companyId}/pwa/icon/${iconType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload pwa icon',
        data: null
      };
    }
  },

  // 14. DELETE PWA ICON (icon192 | icon512)
  deleteCompanyPwaIcon: async (companyId, iconType) => {
    try {
      const response = await axiosInstance.delete(`/companies/${companyId}/pwa/icon/${iconType}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete pwa icon',
        data: null
      };
    }
  }
};

export default CompaniesService;